<template>
  <v-container pa-2>
    <SafariWarning
      v-if="showSafariWarning"
      :auto-close-warning="autoCloseWarning"
    />
    <v-layout justify-center>
      <v-flex xs12 sm8 md8>
        <v-card class="elevation-12">
          <SmoothReflow>
            <TermsItem
              v-if="uploaderState==='terms'"
              @termsAccepted="onTermsAccepted"
            />
            <v-container
              v-if="validateUploadPhotosState()"
              fluid
              class="text-xs-center title font-weight-bold divider-shadow"
            >
              <v-layout>
                <v-flex xs12 align-end flexbox v-if="uploaderState!=='complete'">
                  <span>{{ `${$t('webTransfer.uploadPhotos')}` }} </span>
                </v-flex>
              </v-layout>
            </v-container>
            <v-container v-if="uploaderState ==='invalidGuid' || uploaderState ==='generalError'">
              <v-layout class="text-xs-center title font-weight-bold" id="resultDiv">
                <v-flex v-if="uploaderState==='invalidGuid'" xs12 align-end flexbox>
                  <span>{{$t('qrCode.unableToUploadWithQR')}}</span>
                </v-flex>
                <v-flex v-if="uploaderState==='generalError'" xs12 align-end flexbox>
                  <span>{{$t('qrCode.unexpectedProblem')}}<v-spacer/>{{$t('qrCode.pleaseReload')}}</span>
                </v-flex>
              </v-layout>
              <v-layout v-if="uploaderState==='invalidGuid'" justify-center class="text-xs-center">
                <v-container>
                  <v-spacer/>
                  <span>QR: {{this.guid}}</span>
                  <v-spacer/>
                  <div class="pt-2">
                    <span>{{$t('qrCode.redirectInTen')}}</span>
                    <v-spacer/>
                    <a @click="navigateHome">{{$t('qrCode.redirectNow')}}</a>
                  </div>
                </v-container>
              </v-layout>
            </v-container>
            <v-container
              v-if="validateUploadPhotosState()"
              fluid
              align-center
              justify-center
              column
              px-1
              py-3
            >
              <SmoothReflow>
                <v-layout v-if="!pics.length" align-center justify-center column>
                  <v-flex ma-2 mb-4>
                    <v-img width="220" height="148" d-flex src="@/assets/art/Add Photos Icon.svg"/>
                  </v-flex>
                  <v-flex
                    class="headline font-weight-normal text-xs-center"
                  >{{$t('mediaSelection.addPics')}}</v-flex>
                  <v-flex
                    class="subheading font-weight-normal text-xs-center grey--text"
                  >{{$t('mediaSelection.addPicsHelp')}}</v-flex>
                </v-layout>
                <v-container
                  v-if="pics.length && (uploaderState==='choose-files' || uploaderState==='uploading' || uploaderState==='finalizing' || uploaderState==='needsRetry')"
                  fluid
                  grid-list-xs
                  text-xs-center
                  pa-0
                >
                  <transition-group
                    name="thumbnail-container"
                    tag="div"
                    class="layout row wrap justify-center"
                  >
                    <div
                      v-for="(img, index) in pics"
                      :key="`image-cell-${img.counter}`"
                      :id="`image-cell-${img.counter}`"
                      class="thumbnail-container"
                    >
                      <v-img
                        :src="img.thumbnail"
                        width="104"
                        aspect-ratio="1"
                        class="thumbnail"
                      />
                      <div
                        :class="(img.uploadProgress < 0.0)?'thumbnail-status-frame-inside-error':'thumbnail-status-frame-inside'"
                      />
                      <div
                        :class="(img.uploadProgress < 0.0)?'thumbnail-status-frame-outside-error':'thumbnail-status-frame-outside'"
                      />
                      <div v-if="isDebugMode" class="thumbnail-debug-frame-overlay">{{img.name}}</div>
                      <img
                        v-if="uploaderState==='choose-files'"
                        class="indicator"
                        src="@/assets/art/Remove Photo White.svg"
                        @click="removeFile(index,img.counter)"
                        data-ffid="remove-photo"
                      >
                      <div
                        v-if="uploaderState==='uploading' && (img.uploadProgress < 1.1) && (img.uploadProgress >= 0.0)"
                        class="indicator-progress"
                      >
                        <v-progress-circular
                          size="42"
                          rotate="-90"
                          color="rgba(255,255,255,1.0)"
                          :value="img.uploadProgress*100"
                          class="web-uploader"
                        />
                      </div>
                      <div
                        v-if="(img.uploadProgress >= 1.1)"
                        class="indicator-progress-success"
                        ma-0
                      >
                        <v-img width="62" aspect-ratio="1" src="@/assets/art/Upload Checkmark.svg"/>
                      </div>
                      <div
                        v-if="(img.uploadProgress < 0.0)"
                        pa-0
                        class="indicator-progress-error"
                        ma-0
                      >
                        <v-img width="62" aspect-ratio="1" :src="failedImageOverlay"/>
                      </div>
                    </div>
                    <div
                      v-if="uploaderState==='choose-files'"
                      class="thumbnail-container add-images"
                      data-ffid="add-images-btn"
                      :key="`lastCell`"
                    >
                      <v-img
                        src="@/assets/art/Plus.svg"
                        aspect-ratio="1"
                        class="thumbnail-border"
                        @click="addMoreClick()"
                      ></v-img>
                    </div>
                    <div
                      v-for="index in 50"
                      :key="`flexspacer-${index}`"
                      :id="`flexspacer-${index}`"
                      class="thumbnail-container empty-thumbnail"
                    ></div>
                  </transition-group>
                </v-container>
              </SmoothReflow>
              <v-container
                v-if="uploaderState==='uploading' || uploaderState==='finalizing'"
                class="gtm-upload-in-progress text-center justify-space-between pb-0"
              >
                <v-layout row>
                  <v-flex v-if="uploaderState === 'uploading'">{{$t('webTransfer.estimatedTime')}}: {{calcTimeRemaining()}}</v-flex>
                  <v-flex v-if="uploaderState === 'finalizing'">{{$t('qrCode.finalizingImages')}}</v-flex>
                  <v-flex class="text-xs-right">{{Math.round(bytesSent/bytesToSend*100)}}%</v-flex>
                </v-layout>
                <v-progress-linear
                  background-color="#DEE5EE"
                  background-opacity="1.0"
                  :value="bytesSent/bytesToSend*100"
                  height="8"
                />
              </v-container>
              <div v-if="isDebugMode">
                environment: {{environmentData.environment}}
                <br>
                Total Time to upload {{uploadsSucceeded}} images: {{parseFloat(gtmAnalytics.totalTransferTime).toFixed(2)}}s
                <br>
                Average bandwidth: {{getAverageBandwidth(uploadsSucceeded)}}
                <br>
                Average image size: {{getAverageImageSize(uploadsSucceeded)}}
                <br>
                Last Upload Error: {{lastUploadError}}
                <br>
                Detected Browser: {{clientInfo?.browser?.name || 'unknown'}}
                <br>
                Detected Browser Version: {{clientInfo?.browser?.version || 'unknown'}}
                <br>
                Detected OS: {{clientInfo?.os?.name || 'unknown'}}
                <br>
                Detected OS Version: {{clientInfo?.os?.version || 'unknown'}}
                <br>
                Number of Failed Processed Thumbnails: {{ numFailedProcessedThumbnails }}
              </div>
            </v-container>
            <v-container v-if="uploaderState==='complete'">
              <v-layout class="text-xs-center title font-weight-bold" id="resultDiv">
                <v-flex v-if="!guid" xs12 align-end flexbox>
                  <span v-if="false">{{$t('webTransfer.enterCodeInKiosk')}}</span>
                  <span>{{$t('webTransfer.enterCodeShort')}}</span>
                </v-flex>
                <v-flex v-if="guid" xs12 align-end flexbox>
                  <span class="checkmark">
                    <div class="checkmark_circle"></div>
                    <div class="checkmark_stem"></div>
                    <div class="checkmark_kick"></div>
                  </span>
                  <v-spacer/>
                  <span>{{$t('qrCode.photosUploaded')}}</span>
                  <v-spacer/>
                  &nbsp;
                  <v-spacer/>
                  <h2>{{$t('qrCode.completeAtKiosk')}}</h2>
                </v-flex>
              </v-layout>
              <v-container v-if="!guid" fluid>
                <v-layout row justify-center>
                  <v-spacer/>
                  <v-flex class="code_number">{{batchID[0]}}</v-flex>
                  <v-flex class="code_number">{{batchID[1]}}</v-flex>
                  <v-flex class="code_number">{{batchID[2]}}</v-flex>
                  <v-flex class="code_number">{{batchID[3]}}</v-flex>
                  <v-flex class="code_number">{{batchID[4]}}</v-flex>
                  <v-flex class="code_number">{{batchID[5]}}</v-flex>
                  <v-spacer/>
                </v-layout>
              </v-container>
              <v-layout v-if="!guid" justify-center class="text-xs-center">
                <span>{{$t('sendCode.codeExpires')}} {{getCodeExpiration()}}</span>
              </v-layout>
            </v-container>
            <v-container v-if="uploaderState!=='validateGuid' && uploaderState!=='invalidGuid' && uploaderState!=='generalError'" px-0 pa-0>
              <v-layout align-content-center justify-center px-0>
                <v-container
                  v-if="uploaderState==='choose-files'"
                  align-content-center
                  justify-center
                >
                  <v-layout align-content-center justify-center px-0>
                    <v-btn
                      v-if="!pics.length"
                      @click="addMoreClick();"
                      round
                      color="primary"
                      class="retina"
                      id="btn-select-photos"
                      data-ffid="select-photos-btn"
                    ><span class="retina" id="btn-select-photos">{{$t('webTransfer.selectPhotos')}}</span></v-btn>
                    <v-btn
                      v-if="pics.length"
                      id="sendFile"
                      type="submit"
                      form="frmImport"
                      round
                      color="primary"
                      @click="allowSubmit()"
                      class="gtm-upload-start"
                      data-ffid="start-upload-btn"
                    ><span class="gtm-upload-start">{{$t('webTransfer.startUpload')}}</span></v-btn>
                  </v-layout>
                </v-container>
                <v-layout
                  v-if="uploaderState==='complete'"
                  align-content-center
                  justify-center
                  column
                  pa-0
                >
                  <v-container v-if="!guid" px-0 pt-0>
                    <v-layout align-content-center justify-center>
                      <SendCodeScreen
                        v-if="environmentData.messagingServicesEnabled && !hideSendCodeScreen"
                        :buttonTitle="$t('sendCode.sendMeThisCode')"
                        :code="batchID"
                        :messagingURL="messagingURL"
                        :affiliateID="affiliateID"
                        :appKey="appKey"
                        :authToken="authToken"
                        v-on:messageSent="MessageSent"
                      ></SendCodeScreen>
                    </v-layout>
                  </v-container>
                  <div class="divider-flat"></div>
                  <v-layout v-if="!guid" align-content-center justify-center>
                    <v-btn
                      class="gtm-upload-start-over"
                      flat
                      round
                      color="primary"
                      @click="navigateHome"
                      data-ffid="start-new-upload-btn"
                    >
                      <span class="subheading text-none">{{$t('webTransfer.startnewUpload')}}</span>
                    </v-btn>
                  </v-layout>
                  <v-container v-if="guid" px-0>
                    <v-layout align-content-center justify-center>
                      <span>{{$t('qrCode.needCode')}}<a @click="getUserCode()"> {{$t('qrCode.clickHere')}}</a></span>
                    </v-layout>
                  </v-container>
                </v-layout>
                <div
                  v-if="uploaderState==='needsRetry'"
                  class="gtm-upload-failure text-align-center"
                  id = 'failureDiv'
                >
                  <div class="row text-xs-center">
                    {{$t('webTransfer.retryUpload')}}
                    {{uploadsFailed}}
                    <span
                      v-if="uploadsFailed===1"
                    >{{$t('webTransfer.failedFile')}}</span>
                    <span v-if="uploadsFailed > 1">{{$t('webTransfer.failedFiles')}}</span>
                    <p></p>
                    <!-- {{$t('webTransfer.possibleReasons')}} -->
                  </div>
                  <v-layout v-if="uploadsFailed > 0" justify-center>
                    <div class="row">
                      <v-btn
                        v-if="uploadsFinished>0"
                        round
                        color="primary"
                        @click="fileUploadsFinished();"
                        class="gtm-upload-cancel"
                      ><span class="gtm-upload-cancel">{{$t('common.skip')}}</span></v-btn>
                      <v-btn round color="primary" @click="retryUploads();" class="gtm-upload-retry"><span class="gtm-upload-retry">{{$t('common.retry')}}</span></v-btn>
                    </div>
                  </v-layout>
                </div>
              </v-layout>
            </v-container>
          </SmoothReflow>
        </v-card>
      </v-flex>
    </v-layout>
    <v-layout>
      <v-container py-4 my-2/>
    </v-layout>
    <v-layout v-if="uploaderState==='choose-files' || uploaderState==='terms'"
              text-xs-center align-center>
      <v-container>
        <v-flex>
          <v-img class="mx-auto" width="35" height="28" src="@/assets/art/Wifi_Graphic.svg" />
        </v-flex>
        <v-flex>
          <h3 class="font-weight-regular white--text">{{$t('webTransfer.connectToWifi')}} <span class="font-weight-bold">Walmart Wi-Fi.</span></h3>
        </v-flex>
      </v-container>
    </v-layout>
    <div style="display:none;">
      <div id="selectingPhotosButton text-center justify-content-center">
        <form
          id="frmImport"
          name="frmImport"
          method="POST"
          enctype="multipassets/art/form-data"
          style="display: none;"
        >
          <input type="hidden" name="batchID" id="batchID" :value="batchID">
          <input type="hidden" name="affiliateID" id="affiliateID" value="affiliateID">
          <input type="hidden" name="appKey" id="appKey" value="appKey">
          <input type="hidden" name="uploadURL" id="uploadURL" value="uploadURL">
          <input type="hidden" name="messagingURL" id="messagingURL" value="messagingURL">
          <input
            id="image-input"
            @change="changeEvent"
            type="file"
            :accept="supportedImageTypes.join()"
            multiple
            class="inputfile"
            data-ffid="select-photos-input"
          >
        </form>
      </div>
    </div>
    <FooterItem :style="uploaderState==='terms'?'':'bottom:-55px;'"/>
  </v-container>
</template>
<script>
import SafariWarning from '@/components/SafariWarning'
import TermsItem from '@/components/TermsItem'
import FooterItem from '@/components/FooterItem'
import SendCodeScreen from '@/components/SendCodeScreen'
import FailedImageOverlay from '@/assets/art/Upload Error.svg'
import SmoothReflow from '@/components/SmoothReflow'
import RenderQueue from '@/logic/render-queue'
import Bowser from 'bowser'
import {
  incrementSessionStorageValue,
  savePreviousNumberDataToDatalayer,
  clearSessionStorage,
  isUnexpectedReload
} from '@/logic/sessionStorage'

var loadThumbnails = false
var requestsAtOnce = Math.min(Math.max(1, configData.requestsAtOnce), 4)

const DefaultBandwidthEstimate = 4.0 // in MBits/sec
const moment = require('moment')

// TODO: this is a global context for the vue instance used in this view.  This is not ideal!
let context

export default {
  name: 'ImageTransferApp',
  components: {
    SafariWarning,
    TermsItem,
    FooterItem,
    SendCodeScreen,
    SmoothReflow
  },
  data () {
    let uploaderState = 'terms'
    if (this.$route.name === 'WithGuid') {
      uploaderState = 'validateGuid'
    }
    return {
      isDebugMode:
        new URLSearchParams(window.location.search).get('debug') !== null,
      environmentData: {},

      uploadsSucceeded: 0, // succeeded on the current pass
      uploadsFinished: 0, // succeeded on a previous pass
      uploadsFailed: 0, // failed on this pass

      bytesToSend: 0,
      bytesSent: 0,
      uploadStartTime: 0,

      connectionInfo: {
        interfaceType: '',
        downlink: '',
        rtt: '',
        downlinkMax: '',
        effectiveType: '',
        saveDataUsage: ''
      },

      clientInfo: {},

      internetConnected: true,

      gtmAnalytics: {},
      lastUploadError: '',
      numFailedProcessedThumbnails: 0,

      supportedImageTypes: ['image/jpeg', 'image/png', 'image/heic', 'image/webp'],
      imageTypesRequiringConversion: ['image/heic', 'image/webp'],
      enableImageUploadConversion: false,
      failedImageOverlay: FailedImageOverlay,
      pics: [],
      picsCounter: 0,
      guid: this.$route.params.guid,
      uploaderState,

      batchID: '      ',
      uploadURL: '',
      messagingURL: '',
      appKey: '',
      affiliateID: '',
      authToken: '',
      okayToSubmit: false,

      activeRenderCount: 0,
      hideSendCodeScreen: false,
      isCanada: false,
      renderQueue: null,
      showSafariWarning: false,
      autoCloseWarning: false
    }
  },
  created () {
    context = this
  },
  mounted () {
    this.environmentData = configData
    this.environmentData.environment = configEnv

    context.UUIDCookieName = 'ffcid'
    const ffcid = this.$cookies.get(context.UUIDCookieName)
    if (!ffcid) {
      this.$cookies.set(context.UUIDCookieName, self.crypto.randomUUID(), '1y')
    } else {
      this.$cookies.set(context.UUIDCookieName, ffcid, '1y')
    }

    if (context.guid) {
      this.updateUploaderStateFromGuid()
    }

    if (configData.expiresInDays === undefined) {
      configData.expiresInDays = {
        nonQR: 10,
        qr: 1
      }
    }

    context.renderQueue = new RenderQueue(
      context.onPicThumbnailProcessingCompleted,
      context.onPicThumbnailProcessingFailed
    )

    if (configData.supportedImageTypes?.length) {
      this.supportedImageTypes = configData.supportedImageTypes
    }

    if (configData.imageTypesRequiringConversion?.length) {
      this.imageTypesRequiringConversion = configData.imageTypesRequiringConversion
    }

    if (configData.enableImageUploadConversion !== undefined) {
      this.enableImageUploadConversion = configData.enableImageUploadConversion
    }

    this.isCanada = window.location.hostname.includes('.ca')

    if (this.isCanada) {
      this.hideSendCodeScreen = true
    }

    context.gtmAnalytics.images = []
    context.gtmAnalytics.totalTransferTime = 0

    context.getID()
    context.internetConnected = true // otherwise we would not be running

    $('#frmImport').submit(function (e) {
      e.preventDefault()

      // MS Edge seems to trigger submit every time a file selection occurs.
      // This 'context.okayToSubmit' catch intended to filter those out.
      if (context.okayToSubmit) context.upload()
    })

    context.updateConnectionInfo()

    const urlParams = new URLSearchParams(window.location.search)
    const utmSource = urlParams.get('utm_source')
    const storeNumber = utmSource ? utmSource.replace(/^\D+/g, '') : null // removes all non digits
    window.dataLayer.push({ StoreNumber: storeNumber })

    const workflow = this.$route.name === 'WithGuid' ? 'QR' : 'Standard'
    window.dataLayer.push({ Workflow: workflow })

    const version = this.$route.params.version
    window.dataLayer.push({ Version: version })

    // Push Crash/Reload data to datalayer if it exists.
    if (isUnexpectedReload()) {
      savePreviousNumberDataToDatalayer('thumbnails.total', 'PreviousSessionTotalThumbnails')
      savePreviousNumberDataToDatalayer('thumbnails.success', 'PreviousSessionSuccessThumbnails')
      savePreviousNumberDataToDatalayer('thumbnails.failed', 'PreviousSessionFailedThumbnails')
      savePreviousNumberDataToDatalayer('uploading.total', 'PreviousSessionTotalUploading')
      savePreviousNumberDataToDatalayer('uploading.success', 'PreviousSessionSuccessUploading')
      savePreviousNumberDataToDatalayer('uploading.failed', 'PreviousSessionFailedUploading')
      clearSessionStorage()
    }

    const bowserParser = Bowser.getParser(window.navigator.userAgent)
    this.clientInfo = bowserParser.getResult()

    window.dataLayer.push({
      DeviceOS: this.clientInfo?.os?.name,
      DeviceOSVersion: this.clientInfo?.os?.version,
      DeviceBrowser: this.clientInfo?.browser?.name,
      DeviceBrowserVersion: this.clientInfo?.browser?.version
    })

    const isIOS18 = bowserParser.satisfies({
      iOS: {
        safari: '>=18.0'
      }
    })

    const isSafeIOS18 = bowserParser.satisfies({
      iOS: {
        safari: '>=18.2'
      }
    })

    this.showSafariWarning = isIOS18 && !isSafeIOS18
  },
  methods: {
    navigateHome () {
      const version = this.$route.params.version
      const env = this.$route.query.env
      this.$router.replace({ name: 'Home', params: { version }, query: { env } })
      window.location.reload()
    },
    onTermsAccepted () {
      this.uploaderState = 'choose-files'
      this.autoCloseWarning = true
    },
    onPicThumbnailProcessingCompleted (dataURL, picId) {
      // We need to make sure that we put the thumbnail on the correct pic. In the case where we have
      // a thumbnail error or the user removes a picture before the thumbnail is completed, we must
      // find the correct pic and set the thumbnail dataURL
      const pic = this.pics.find((p) => p.counter === picId)
      if (pic) {
        pic.thumbnail = dataURL
      }
      incrementSessionStorageValue('thumbnails.success')
    },
    onPicThumbnailProcessingFailed (error) {
      this.lastUploadError = error.message
      this.numFailedProcessedThumbnails++
      incrementSessionStorageValue('thumbnails.failed')
      window.dataLayer.push({ 'thumbnail_processing_error': error.message })
      window.dataLayer.push({ event: 'thumbnail_processing_failed' })
    },
    getID () {
      $('#affiliateID').val((this.affiliateID = configData.affiliateID))
      $('#appKey').val((this.appKey = configData.appKey))
      $('#uploadURL').val((this.uploadURL = configData.uploadURL))
      $('#messagingURL').val((this.messagingURL = configData.messagingURL))
      if (configData.loadThumbnails) {
        loadThumbnails = configData.loadThumbnails
      }
    },

    validateUploadPhotosState () {
      return this.uploaderState !== 'complete' &&
             this.uploaderState !== 'terms' &&
             this.uploaderState !== 'validateGuid' &&
             this.uploaderState !== 'invalidGuid' &&
             this.uploaderState !== 'generalError'
    },

    updateConnectionInfo () {
      // record information about the network connection in use for browsers that support it.
      if (navigator && navigator.connection) {
        this.connectionInfo.interfaceType = navigator.connection.type + ''
        this.connectionInfo.downlink = navigator.connection.downlink + ' Mb/s'
        this.connectionInfo.rtt = navigator.connection.rtt + ' ms'
        this.connectionInfo.downlinkMax =
          navigator.connection.downlinkMax + ' Mb/s'
        this.connectionInfo.effectiveType =
          navigator.connection.effectiveType + ''
        this.connectionInfo.saveDataUsage = navigator.connection.saveData + ''
      }

      this.gtmAnalytics.connectionInfo = this.connectionInfo
    },

    getAverageBandwidth (count) {
      var bw = 0.0
      if (this.gtmAnalytics.images && count > 0) {
        bw =
          this.gtmAnalytics.images.reduce((prev, cur) => {
            return prev + (cur.size * 8) / (1024 * 1024 * cur.transferTime)
          }, 0) / count
      }
      return `${bw.toFixed(2)}` // megabits per sec
    },

    getAverageImageSize (count) {
      var size = 0.0
      if (this.gtmAnalytics.images && count > 0) {
        size =
          context.gtmAnalytics.images.reduce((prev, cur) => {
            return prev + cur.size / (1024 * 1024)
          }, 0) / count
      }
      return `${size.toFixed(2)}`
    },

    retryUploads: function () {
      // re-total the byte count for the failed uploads to ensure the overall progress percentage is correct
      // and clear out the partial upload byte counts
      context.bytesToSend = 0
      context.pics.forEach(element => {
        if (element.uploadProgress < 0) {
          // failed uploads are flagged as -1
          context.bytesToSend += element.file.size
          element.uploadedBytes = 0
        }
      })

      sessionStorage.setItem('uploading.failed', 0)
      sessionStorage.setItem('uploading.success', context.uploadsFinished)

      context.uploadsFailed = 0
      context.uploadsSucceeded = 0
      context.bytesSent = 0
      context.uploaderState = 'choose-files' // re-activate the send files button
      this.$nextTick(() => {
        // let Vue update the Dom so the button will exist again
        $('#sendFile').click()
      })
    },

    cancelUploads () {
      context.uploaderState = 'choose-files'
      context.startOver()
    },

    /*
    startUploadsFromFailure () {
      context.uploaderState = 'uploading'
      context.fileUploadsFinished()
    },
    */

    fileUploadsFinished () {
      let uploadBatchResult = null
      if (context.guid) {
        uploadBatchResult = context.createUploadBatchWithGuid()
        context.createAndUpdateBatchUploadStatus('BatchUploadCompleted')
      } else {
        uploadBatchResult = context.createUploadBatch()
      }

      // Clear the session storage because at this point we have successfully completed the session
      clearSessionStorage()

      // why can't I get anything from this response?
      var affiliateID = context.affiliateID
      var appKey = context.appKey

      uploadBatchResult.done(function (file2, status2, xhr2) {
        context.batchID = JSON.parse(xhr2.responseText).Data.BatchIdentifier
        if (context.environmentData.messagingServicesEnabled && !context.guid) {
          let deliverTo = 'multiple'
          context.createAuthToken(
            appKey,
            affiliateID,
            context.batchID,
            deliverTo
          )
        }
        let timeoutMS = 2000

        if (context.guid) {
          context.uploaderState = 'finalizing'
          timeoutMS = context.environmentData.finalizingImagesTimeoutSeconds * 1000
        }
        setTimeout(() => {
          context.uploaderState = 'complete'
          this.pics = []

          context.$nextTick(() => {
            context.$vuetify.goTo(0, { duration: 500 })
          })
        }, timeoutMS)
      })
    },

    updateUploaderStateFromGuid () {
      const validateGuidResult = this.createAndUpdateBatchUploadStatus('BatchUploadReceived', this.$cookies.get(context.UUIDCookieName))
      validateGuidResult.done((file2, status2, xhr2) => {
        const validateGuidStatus = JSON.parse(xhr2.responseText).Status
        if (validateGuidStatus.Code === 200) {
          this.uploaderState = 'choose-files'
        } else if (validateGuidStatus.Code === 4277) {
          this.uploaderState = 'invalidGuid'
          // Redirects back to root web transfer webapp
          setTimeout(() => {
            this.navigateHome()
          }, 10000)
        } else {
          this.uploaderState = 'generalError'
        }
      })
      validateGuidResult.fail(function (xhr, status, errMsg) {
        this.uploaderState = 'generalError'
      })
    },

    getUserCode () {
      const validateGuidResult = context.convertUploadBatchGuidToUserCode()

      var affiliateID = context.affiliateID
      var appKey = context.appKey

      validateGuidResult.done(function (file2, status2, xhr2) {
        context.batchID = JSON.parse(xhr2.responseText).Data.BatchIdentifier
        if (context.environmentData.messagingServicesEnabled) {
          let deliverTo = 'multiple'
          context.createAuthToken(
            appKey,
            affiliateID,
            context.batchID,
            deliverTo
          )
        }
        context.guid = ''
        setTimeout(() => {
          context.uploaderState = 'complete'
          this.pics = []

          context.$nextTick(() => {
            context.$vuetify.goTo(0, { duration: 500 })
          })
        }, 2000)
      })
    },

    convertUploadBatchGuidToUserCode () {
      // Construct the endpoint.
      const convertUploadBatchGuidToUserCodeEndpoint = `${configData.uploadURL}batches/${context.guid}?op=convertToUserCode`

      // Send the request and return the response.
      return jQuery.ajax({
        url: convertUploadBatchGuidToUserCodeEndpoint,
        type: 'POST',

        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: 'Fujifilm ApiKey=' + configData.appKey
        }
      })
    },

    createAuthToken (appKey, affiliateID, userID, deliverTo) {
      let messagingAuthEndpoint = this.messagingURL + 'auth/' // 'https://is.fujifilmapi.com/rest/v1/messaging/auth/'
      let dataBlob = {
        AffiliateID: affiliateID,
        Referrer: 'PhotoTransferSite',
        UserID: userID,
        DeliverTo: deliverTo,
        ClientNonce: 'PTSDelivery'
      }

      let messagingAuthResult = jQuery.ajax({
        url: messagingAuthEndpoint,
        type: 'POST',
        data: JSON.stringify(dataBlob),
        processData: false,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: 'Fujifilm ApiKey=' + appKey
        }
      })
      messagingAuthResult.done(function (file2, status2, xhr2) {
        context.authToken = JSON.parse(xhr2.responseText).Data.AuthToken
      })
      // end of messagingAuthResult, which is step 1:
      messagingAuthResult.fail(function (file2, status2, xhr2) {
        console.error('Failed creating authtoken.')
      })
    },

    checkInternetConnection () {
      $.ajax({
        url: window.location.protocol + '//' + window.location.host,
        type: 'GET',
        cache: false,
        success: function () {
          context.internetConnected = true
        },
        error: function () {
          context.internetConnected = false
        }
      })
    },

    createUploadBatch () {
      // Construct the endpoint.
      var uploadBatchEndpoint = configData.uploadURL
      uploadBatchEndpoint = uploadBatchEndpoint + 'batches/'

      var fileIDsArray = []
      if (this.pics) {
        for (var i = 0; i < this.pics.length; i++) {
          if (this.pics[i].fileId) {
            fileIDsArray.push(this.pics[i].fileId)
          }
        }
      }

      // Send the request and return the response.
      return jQuery.ajax({
        url: uploadBatchEndpoint,
        type: 'PUT',
        data: JSON.stringify(fileIDsArray),
        processData: false,

        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: 'Fujifilm ApiKey=' + configData.appKey
        }
      })
    },

    createUploadBatchWithGuid () {
      // Construct the endpoint.
      var uploadBatchEndpoint = configData.uploadURL
      uploadBatchEndpoint = uploadBatchEndpoint + 'batches/' + context.guid

      var fileIDsArray = []
      if (this.pics) {
        for (var i = 0; i < this.pics.length; i++) {
          if (this.pics[i].fileId) {
            fileIDsArray.push(this.pics[i].fileId)
          }
        }
      }

      // Send the request and return the response.
      return jQuery.ajax({
        url: uploadBatchEndpoint,
        type: 'PATCH',
        data: JSON.stringify(fileIDsArray),
        processData: false,

        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: 'Fujifilm ApiKey=' + configData.appKey
        }
      })
    },

    /**
     * @param {string} status the status of the batch
     * @param {string?} UniqueUserID the unique identifier of the customer if applicable
     *
     */
    createAndUpdateBatchUploadStatus (status, UniqueUserID) {
      // Construct the endpoint.
      var createAndUpdateBatchUploadStatusEndpoint = configData.uploadURL
      createAndUpdateBatchUploadStatusEndpoint = `${createAndUpdateBatchUploadStatusEndpoint}batches/${context.guid}/status/${status}`

      // Send the request and return the response.
      return jQuery.ajax({
        url: createAndUpdateBatchUploadStatusEndpoint,
        type: 'PATCH',
        data: JSON.stringify({ UniqueUserID }),
        contentType: 'application/json',

        headers: {
          Authorization: 'Fujifilm ApiKey=' + configData.appKey
        }
      })
    },

    // Display error messages.
    onError (error) {
      // alert(error.responseText);
      // eslint-disable-next-line
      console.error(error);
    },

    startOver () {
      this.uploadsSucceeded = 0
      this.uploadsFinished = 0
      this.uploadsFailed = 0
      if (!this.isCanada) {
        this.hideSendCodeScreen = false
      }

      this.bytesSent = 0
      this.bytesToSend = 0

      this.lastUploadError = ''

      this.pics = []
      this.picsCounter = 0
      this.uploaderState = 'choose-files'
      this.batchID = '      '
      this.activeRenderCount = 0
      this.okayToSubmit = false

      // clear out analytic data for next upload
      context.gtmAnalytics = {}
      context.updateConnectionInfo()
      context.gtmAnalytics.images = []
      context.gtmAnalytics.totalTransferTime = 0
    },

    isSupportedImage (file) {
      // Heic/Heif files won't report a MIME type so we'll have to check the file extension
      const fileType = this.getFileType(file)
      if (context.supportedImageTypes.indexOf(fileType) === -1) {
        console.error('Unsupported image type: ' + fileType)
        return false
      }
      return true
    },

    /**
     * Gets file's MIME type.
     * For browsers that do not natively support HEIC/HEIF, the 'type' property will not be defined.
     * We need to manually check the file extension. The begining of the base64 string for HEIC/HEIF files is
     * 'application/octet-stream,' so that does not help us much.
     */
    getFileType (file) {
      let fileType = file.type
      if (!fileType) {
        const extension = file.name.split('.').pop().toLowerCase()
        if (extension === 'heic' || extension === 'heif') {
          fileType = 'image/heic'
        }
      }
      return fileType
    },

    doesImageNeedConversion (file) {
      const fileType = this.getFileType(file)
      return this.imageTypesRequiringConversion.includes(fileType)
    },

    cumulativeOffset (element) {
      var top = 0
      var left = 0
      do {
        top += element.offsetTop || 0
        left += element.offsetLeft || 0
        element = element.offsetParent
      } while (element)

      return {
        top: top,
        left: left
      }
    },

    absolutize (element) {
      if (element.style.position && element.style.position === 'absolute') {
        return element
      }

      let elementStyle =
        element.currentStyle || window.getComputedStyle(element)

      let offset = context.cumulativeOffset(element)

      offset.top = element.offsetTop - parseFloat(elementStyle.marginTop) // assumes pixels
      offset.left = element.offsetLeft - parseFloat(elementStyle.marginLeft) // assumes pixels

      element.style.top = offset.top + 'px'
      element.style.left = offset.left + 'px'

      return element
    },

    removeFile (index, counter) {
      let element = document.getElementById(`image-cell-${counter}`)
      if (element) {
        context.absolutize(element)
        element.classList.add('thumbnail-container-removed-item')
      }
      this.pics.splice(index, 1)
    },

    allowSubmit () {
      context.okayToSubmit = true
      return true
    },

    // upload.js  **************************************************************************************************************
    upload: function (/* affiliateID, appKey, uploadURL */) {
      if (context.guid) { // If there was a provided guid, update status
        context.createAndUpdateBatchUploadStatus('BatchUploadStarted')
      }

      sessionStorage.setItem('uploading.total', context.pics.length)

      // clear the queue so the uploading isn't CPU limited
      context.renderQueue.clear()

      var retryCount = 1
      let isRetry = context.bytesToSend > 0
      context.gtmAnalytics.totalTransferTime = 0
      context.uploadStartTime = new Date()

      window.onbeforeunload = function () {
        return true
      }

      // total up the file sizes for progress usage
      if (!isRetry) {
        context.pics.forEach((element, index) => {
          context.bytesToSend += element.file.size
        })
        const megabytesToSend = context.bytesToSend / 1048576 // 1024*1024 = 1048576
        window.dataLayer.push({ TotalAttemptedUploadSizeMB: megabytesToSend })
      }

      var filesToWorkOn = []

      context.uploadsFinished = 0
      context.pics.forEach((element, index) => {
        if (element.fileId) {
          context.uploadsFinished++
        } else {
          filesToWorkOn.push({
            counter: element.counter,
            index: element.index,
            picsIndex: index
          })
        }
      })

      let visibleFileCount = filesToWorkOn.length
      if (visibleFileCount === 0) {
        if (context.uploadsFinished > 0) {
          context.uploaderState = 'uploading'
          handleUploadsFinished()
        } else {
          return
        }
      }

      context.uploaderState = 'uploading'
      var inUse = 0

      var uploadInterval = setInterval(function () {
        if (inUse >= requestsAtOnce) {
          return
        }

        if (filesToWorkOn.length === 0) {
          clearInterval(uploadInterval)
          return
        }

        inUse++
        var fileToWorkOn = filesToWorkOn.shift()
        var callback = function () {
          inUse--
        }
        context.pics[fileToWorkOn.picsIndex].uploadProgress = 0
        uploadFile(
          context.pics[fileToWorkOn.picsIndex].file,
          fileToWorkOn.picsIndex,
          isRetry,
          callback,
          retryCount
        )
      }, 200)

      function checkIfDoneUploading () {
        return (
          context.pics.length ===
          context.uploadsSucceeded +
            context.uploadsFailed +
            context.uploadsFinished
        )
      }

      function handleUploadsFinished () {
        // context.checkInternetConnection()
        context.uploadsFinished += context.uploadsSucceeded
        let elapsed = (new Date() - context.uploadStartTime) / 1000
        context.gtmAnalytics.totalTransferTime = elapsed
        context.gtmAnalytics.averageBandwidth = context.getAverageBandwidth(
          context.uploadsSucceeded
        )

        const megabytesSent = context.bytesSent / 1048576 // 1024*1024 = 1048576
        window.dataLayer.push({ TotalSuccessfulUploadSizeMB: megabytesSent })
        window.dataLayer.push({ TotalSuccessfulUploadCount: context.uploadsFinished })

        const isTotalFailure = context.uploadsFailed === context.pics.length
        window.dataLayer.push({ 'is_total_failure': isTotalFailure })
        window.dataLayer.push({ 'total_image_count': context.pics.length })
        window.dataLayer.push({ 'error_image_count': context.uploadsFailed })
        window.dataLayer.push({ 'AverageBandwidth': context.gtmAnalytics.averageBandwidth })
        window.dataLayer.push({ 'TotalTransferTime': context.gtmAnalytics.totalTransferTime })
        window.dataLayer.push({ 'AverageImageSize': context.getAverageImageSize(context.uploadsSucceeded) })

        window.onbeforeunload = null
        if (context.uploadsFailed > 0) {
          context.uploaderState = 'needsRetry'
        } else {
          context.fileUploadsFinished()
        }
      }

      function uploadFile (file, counter, isRetry, callback, retryCount) {
        const needsConversion = context.doesImageNeedConversion(file) && context.enableImageUploadConversion
        const fileType = context.getFileType(file)
        var expiresInDays = context.getExpiresInDays()
        var url = context.uploadURL
        url +=
          '?expireInDays=' +
          expiresInDays +
          '&affiliateid=' +
          context.affiliateID +
          '&debug=' +
          context.isDebugMode +
          '&convertToJpeg=' +
          needsConversion
        var getFile = getFileBuffer(file)
        var startTime
        var endTime
        // Send the request and return the response.
        var onFileReadSuccess = (buffer) => {
          $.ajax({
            xhr: function () {
              var xhr = new window.XMLHttpRequest()
              // Upload progress
              xhr.upload.addEventListener(
                'progress',
                function (evt) {
                  if (evt.lengthComputable) {
                    let pic = context.pics[counter]
                    // clear abort timeout
                    if (pic.abortAction) {
                      clearTimeout(pic.abortAction)
                    }
                    let percentComplete = evt.loaded / evt.total
                    pic.uploadProgress = percentComplete
                    // the following is rather unintuitive (for most of us)
                    // we send multiple files at the same time via threading.
                    // we cannot simply add the total bytes sent for each segment,
                    // because they'd get counted multiple times per file. we need to record
                    // the incremental progress for this file segment vs all previous segments sent.
                    context.bytesSent += evt.loaded - pic.uploadedBytes
                    pic.uploadedBytes = evt.loaded
                    // set abort timeout
                    var timeoutInMilliseconds =
                      configData.fileUploadIdleTimoutInSeconds * 1000
                    pic.abortAction = setTimeout(function () {
                      console.error('Upload timed out for ' + pic.name + ' ...aborting')
                      xhr.abort()
                    }, timeoutInMilliseconds)
                  }
                },
                false
              )
              // clear abort timeout if the image uploaded successfully
              xhr.upload.addEventListener(
                'load',
                function (evt) {
                  let pic = context.pics[counter]
                  if (pic.abortAction) {
                    clearTimeout(pic.abortAction)
                  }
                },
                false
              )
              // Download progress
              xhr.addEventListener('progress', function (evt) {}, false)
              return xhr
            },
            url: url,
            headers: {
              Accept: 'application/json',
              'Content-Type': fileType,
              Authorization: 'Fujifilm ApiKey=' + context.appKey
            },
            type: 'POST',
            dataType: 'json',
            data: buffer,
            processData: false,
            beforeSend: () => {
              startTime = new Date()
            }
          })
            .done((data, textStatus, jqXHR) => {
              endTime = new Date()
              var elapsed = endTime - startTime
              elapsed /= 1000
              let pic = context.pics[counter]
              clearTimeout(pic.abortAction)
              pic.abortAction = null
              pic.uploadProgress = 1.1
              // setTimeout(function () {
              //   pic.uploadProgress = 1.1
              // }, 1000) // allow the progress to show finished before closing it off
              context.uploadsSucceeded++
              incrementSessionStorageValue('uploading.success')
              callback(elapsed, file.size)
              var fileId = data.Data.FileId
              pic.fileId = fileId
              if (fileId) {
                addTransferAnalyticData(file, fileId, elapsed, counter)
              }
              if (checkIfDoneUploading()) {
                handleUploadsFinished()
              }
            })
            .fail((jqXHR, textStatus, errorThrown) => {
              if (retryCount > 0) {
                retryCount--
                uploadFile(file, counter, isRetry, callback, retryCount)
              } else {
                callback()
                context.lastUploadError = jqXHR
                let pic = context.pics[counter]
                clearTimeout(pic.abortAction)
                pic.uploadProgress = -1
                context.uploadsFailed++
                incrementSessionStorageValue('uploading.failed')
                if (checkIfDoneUploading()) {
                  handleUploadsFinished()
                }
              }
            })
        }

        getFile.done(onFileReadSuccess)

        getFile.fail(e => {
          context.pics[counter].uploadProgress = -1
          context.uploadsFailed++
          incrementSessionStorageValue('uploading.failed')
        })
      }

      // returns bandwidth in Mbps (M bits per sec)
      function calculateBandwidth (bytesTransfered, timeInSeconds) {
        var bw = (bytesTransfered * 8) / (1024 * 1024 * timeInSeconds)
        return bw
      }

      function addTransferAnalyticData (file, fileId, transferTime, index) {
        var images = context.gtmAnalytics.images
        var data = {
          index: index,
          fileId: fileId,
          name: file.name,
          size: file.size,
          transferTime: transferTime,
          bandwidth: calculateBandwidth(file.size, transferTime)
        }
        images.push(data)
      }

      // Get the local file as an array buffer.
      function getFileBuffer (file) {
        var deferred = jQuery.Deferred()
        var reader = new FileReader()

        reader.onloadend = function (e) {
          deferred.resolve(e.target.result)
        }
        reader.onerror = function (e) {
          deferred.reject(e.target.error)
        }

        reader.readAsArrayBuffer(file)
        return deferred.promise()
      }
    },

    calcTimeRemaining () {
      let result = ''
      if (
        context.uploadsSucceeded < requestsAtOnce ||
        context.bytesSent < 4 * 1024 * 1024
      ) {
        return window.i18n.t('webTransfer.calculating') // too early to estimate
      }
      let bw = context.getAverageBandwidth(context.uploadsSucceeded)
      bw = parseFloat(bw)
      if (Math.round(bw) === 0) {
        // should not happen, but best to safeguard
        bw = DefaultBandwidthEstimate
      }
      bw /= 8 // convert bits per sec to bytes per sec
      let bytesLeft = context.bytesToSend - context.bytesSent
      let timeLeftInSecs = bytesLeft / (1024 * 1024) / bw
      timeLeftInSecs = Math.round(timeLeftInSecs)
      if (timeLeftInSecs > 60) {
        let timeLeftInMins = Math.round(timeLeftInSecs / 60)
        timeLeftInSecs = Math.round(timeLeftInSecs % 60)
        // use context.i18n? English, Spanish, French, & Portuguese all use 'm' & 's'
        result = timeLeftInMins + 'm '
      }
      result += timeLeftInSecs + 's '

      return result
    },

    getExpiresInDays () {
      if (this.$route.name === 'WithGuid') {
        return configData.expiresInDays.qr || 1
      }
      return configData.expiresInDays.nonQR || 10
    },

    getCodeExpiration () {
      const expiresInDays = this.getExpiresInDays()
      let expDate = moment().add(expiresInDays, 'days')
      let result = expDate.format('LT')
      result += ' ' + window.i18n.t('common.onDate') + ' '
      result += expDate.format('l')
      return result
    },

    // custom-file-input.js  ***************************************************************************************************
    // By Osvaldas Valutis, www.osvaldas.info
    // Available for use under the MIT License
    // This code has been heavily modified for Vue and Vuetify since that original starting point

    addMoreClick () {
      $('#image-input').click()
      this.autoCloseWarning = true
    },

    changeEvent (e) {
      const validImages = Array.from(e.target.files).filter(f => context.isSupportedImage(f))

      if (validImages.length === 0) {
        return
      }

      incrementSessionStorageValue('thumbnails.total', validImages.length)

      for (let i = 0; i < validImages.length; i++) {
        const file = validImages[i]

        const pic = {
          counter: context.picsCounter++,
          index: i,
          name: file.name,
          size: file.size,
          uploadProgress: undefined,
          uploadedBytes: 0,
          file: file,
          thumbnail: ''
        }

        context.pics.push(pic)
        if (loadThumbnails) {
          context.renderQueue.add(pic)
        }
      }
      const curNames = context.pics.reduce((prev, cur) => {
        if (prev !== '') {
          prev = prev + ', '
        }
        return prev + cur.name
      }, '')

      window.dataLayer.push({ 'total_image_count': context.pics.length })
      window.dataLayer.push({ 'images_added_count': validImages.length })
      window.dataLayer.push({ event: 'after_select_photos' })

      window.dataLayer.push({ 'FetchNames': curNames })
      window.dataLayer.push({ 'FetchCount': context.picsCounter })

      setTimeout(() => {
        context.$nextTick(() => {
          context.$vuetify.goTo(
            document.getElementById('app').offsetHeight -
              window.innerHeight -
              50,
            { duration: 500 }
          )
        })
      }, 750)
    },

    MessageSent (value) {
      context.hideSendCodeScreen = value
    }
  }
}
</script>

<style scoped>
</style>
